// src/pages/Solutions/CreateSolutionPage.js
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, TextField, Button, Box, Grid,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  CircularProgress, Alert, Chip, Divider, IconButton, GlobalStyles,
  Dialog, DialogContent, Slide, Checkbox, ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ArrowBack, Save, Add, Delete, CheckCircle, Close, Launch } from '@mui/icons-material';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';

// Rengigs Official Logo Palette - Only Green Shades
const rengigsPalette = {
  forestGreen: '#2E7D32',
  deepForest: '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A',
  brandLime: '#CDDC39',
  charcoal: '#1A2A3A',
  softGreen: 'rgba(46, 125, 50, 0.06)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
  gradientReverse: 'linear-gradient(135deg, #CDDC39 0%, #8BC34A 40%, #4CAF50 70%, #2E7D32 100%)',
};

const scrollbarStyles = {
  '*::-webkit-scrollbar': { width: '10px', height: '10px' },
  '*::-webkit-scrollbar-track': { background: rengigsPalette.offWhite, borderRadius: '10px' },
  '*::-webkit-scrollbar-thumb': {
    background: rengigsPalette.gradient, borderRadius: '10px', transition: 'all 0.3s ease',
  },
  '*::-webkit-scrollbar-thumb:hover': { background: rengigsPalette.gradientReverse },
  '*': { scrollbarWidth: 'thin', scrollbarColor: `${rengigsPalette.forestGreen} ${rengigsPalette.offWhite}` },
};

const validationSchema = yup.object({
  title: yup.string().required('Title is required').max(255),
  tagline: yup.string().max(255),
  description: yup.string().required('Description is required').max(5000),
  solution_type: yup.string().required('Solution type is required'),
  pricing_model: yup.string().required('Pricing model is required'),
  price: yup.number().min(0).required('Price is required'),
  currency: yup.string().default('USD'),
  setup_fee: yup.number().min(0).default(0),
  trial_days: yup.number().min(0).default(0),
  implementation_time: yup.string(),
  api_available: yup.boolean().default(false),
  training_available: yup.boolean().default(false),
});

const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CreateSolutionPage = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [createdSolutionTitle, setCreatedSolutionTitle] = useState('');
  const [createdSolutionId, setCreatedSolutionId] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [orgFetchError, setOrgFetchError] = useState(null);
  const [features, setFeatures] = useState(['']);
  const [benefits, setBenefits] = useState(['']);
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchOrganizations();
  }, []);

  // Auto-close dialog after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        handleCloseDialog();
      }, 4000);
      setAutoCloseTimer(timer);
      
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [success]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await api.get('/core/categories/');
      console.log('Categories API Response:', data);
      
      // Handle different response formats
      let categoriesList = [];
      if (Array.isArray(data)) {
        categoriesList = data;
      } else if (data?.results && Array.isArray(data.results)) {
        categoriesList = data.results;
      } else if (data && typeof data === 'object') {
        categoriesList = Object.values(data).filter(item => item && typeof item === 'object' && item.id);
      }
      
      // Filter to get main categories (parent_id is null)
      // Also include categories without parent_id or with parent_id = null
      const mainCategories = categoriesList.filter(cat => 
        cat && (cat.parent_id === null || cat.parent_id === undefined || cat.parent_id === '')
      );
      
      console.log('Main Categories:', mainCategories);
      setCategories(mainCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      setOrgFetchError(null);

      let orgs = [];
      try {
        const data = await api.get('/organizations/my-organizations/');
        if (Array.isArray(data)) {
          orgs = data;
        } else if (data?.results && Array.isArray(data.results)) {
          orgs = data.results;
        } else if (data?.id) {
          orgs = [data];
        } else if (typeof data === 'object' && data !== null) {
          const firstArray = Object.values(data).find(v => Array.isArray(v));
          orgs = firstArray || [];
        }
      } catch (primaryErr) {
        console.warn('my-organizations failed, trying fallback:', primaryErr);
        try {
          const fallback = await api.get('/organizations/');
          const list = Array.isArray(fallback) ? fallback : fallback?.results || [];
          orgs = list;
        } catch (fallbackErr) {
          console.error('Both org endpoints failed:', fallbackErr);
          setOrgFetchError('Could not load your organizations. You can still fill in the form.');
        }
      }

      setOrganizations(orgs);

      if (orgs.length === 1) {
        formik.setFieldValue('provider_id', orgs[0].id);
      }
    } catch (err) {
      console.error('Error in fetchOrganizations:', err);
      setOrgFetchError('Could not load organizations.');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleAddFeature = () => setFeatures([...features, '']);
  const handleRemoveFeature = (index) => {
    const newFeatures = features.filter((_, i) => i !== index);
    setFeatures(newFeatures.length ? newFeatures : ['']);
  };
  const handleFeatureChange = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  const handleAddBenefit = () => setBenefits([...benefits, '']);
  const handleRemoveBenefit = (index) => {
    const newBenefits = benefits.filter((_, i) => i !== index);
    setBenefits(newBenefits.length ? newBenefits : ['']);
  };
  const handleBenefitChange = (index, value) => {
    const newBenefits = [...benefits];
    newBenefits[index] = value;
    setBenefits(newBenefits);
  };

  const handleCloseDialog = () => {
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
    }
    setSuccess(false);
    navigate('/solutions');
  };

  const handleViewSolution = () => {
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
    }
    setSuccess(false);
    navigate(`/solutions/${createdSolutionId}`);
  };

  const formik = useFormik({
    initialValues: {
      title: '', tagline: '', description: '', solution_type: '',
      provider_id: '', pricing_model: 'subscription_monthly',
      price: '', currency: 'USD', setup_fee: 0, trial_days: 0,
      implementation_time: '', api_available: false,
      training_available: false, video_url: '', api_documentation: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        setError(null);

        const resolvedProviderId = values.provider_id || (organizations.length === 1 ? organizations[0].id : '');

        if (!resolvedProviderId) {
          setError('Please select a provider organization.');
          setSubmitting(false);
          return;
        }

        const solutionData = {
          title: values.title,
          tagline: values.tagline || '',
          description: values.description,
          solution_type: values.solution_type,
          provider_id: resolvedProviderId,
          pricing_model: values.pricing_model,
          price: parseFloat(values.price) || 0,
          currency: values.currency || 'USD',
          setup_fee: parseFloat(values.setup_fee) || 0,
          trial_days: parseInt(values.trial_days) || 0,
          implementation_time: values.implementation_time || '',
          api_available: values.api_available,
          training_available: values.training_available,
          video_url: values.video_url?.trim() || null,
          api_documentation: values.api_documentation?.trim() || null,
          key_features: features.filter(f => f.trim() !== ''),
          benefits: benefits.filter(b => b.trim() !== ''),
          category_ids: selectedCategories.map(c => c.id),
        };

        const response = await api.post('/solutions/', solutionData);
        
        setCreatedSolutionTitle(values.title);
        setCreatedSolutionId(response.id || '');
        setSuccessMessage('Your solution has been successfully listed on Rengigs');
        setSuccess(true);
        
      } catch (err) {
        if (err.data && typeof err.data === 'object') {
          const errorMessages = Object.entries(err.data)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          setError(errorMessages || 'Failed to create solution');
        } else {
          setError(err.message || 'Failed to create solution');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loadingOrgs) {
    return (
      <>
        <GlobalStyles styles={scrollbarStyles} />
        <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: rengigsPalette.forestGreen }} />
        </Container>
      </>
    );
  }

  return (
    <>
      <GlobalStyles styles={scrollbarStyles} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/solutions')}
          sx={{ mb: 2, color: rengigsPalette.charcoal, fontWeight: 800, textTransform: 'none' }}
        >
          Back to Solutions
        </Button>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, border: `1px solid ${rengigsPalette.border}` }}>
          <Box sx={{ mb: 4, p: 3, bgcolor: rengigsPalette.offWhite, borderRadius: 2, borderLeft: `6px solid ${rengigsPalette.forestGreen}` }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: rengigsPalette.charcoal, letterSpacing: '-0.02em' }}>
              List Your Solution
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', fontWeight: 500 }}>
              Submit industrial-grade specifications for the Rengigs network.
            </Typography>
          </Box>

          {orgFetchError && (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
              {orgFetchError}
            </Alert>
          )}

          {!orgFetchError && organizations.length === 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate('/organizations/create')}
                >
                  Create Org
                </Button>
              }
            >
              No organizations found for your account. You need an organization to list a solution.
              {' '}You can create one and come back, or if this is an error, try refreshing.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{error}</pre>
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>

              {/* ── Core Details ── */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.charcoal, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: rengigsPalette.forestGreen }} /> Core Details
                </Typography>
                <Divider sx={{ mt: 1, mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="title" label="Solution Title *"
                  value={formik.values.title} onChange={formik.handleChange}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                {organizations.length > 0 ? (
                  <FormControl fullWidth error={formik.touched.provider_id && Boolean(formik.errors.provider_id)}>
                    <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Provider Organization *</InputLabel>
                    <Select
                      name="provider_id" value={formik.values.provider_id}
                      onChange={formik.handleChange} label="Provider Organization *"
                      disabled={submitting}
                      sx={{ borderRadius: '12px' }}
                    >
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.provider_id && formik.errors.provider_id && (
                      <FormHelperText>{formik.errors.provider_id}</FormHelperText>
                    )}
                  </FormControl>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: '12px', height: '100%' }}>
                    No organizations linked to your account.{' '}
                    <Button size="small" onClick={() => navigate('/organizations/create')}
                      sx={{ color: rengigsPalette.forestGreen, p: 0, textTransform: 'none', fontWeight: 700 }}>
                      Create one
                    </Button>
                    {' '}to enable solution listing.
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={formik.touched.solution_type && Boolean(formik.errors.solution_type)}>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Solution Type *</InputLabel>
                  <Select
                    name="solution_type" value={formik.values.solution_type}
                    onChange={formik.handleChange} label="Solution Type *"
                    disabled={submitting}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="product">Product</MenuItem>
                    <MenuItem value="service">Service</MenuItem>
                    <MenuItem value="software">Software</MenuItem>
                    <MenuItem value="hardware">Hardware</MenuItem>
                    <MenuItem value="consulting">Consulting</MenuItem>
                  </Select>
                  {formik.touched.solution_type && formik.errors.solution_type && (
                    <FormHelperText>{formik.errors.solution_type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Categories Section */}
              <Grid item xs={12} md={6}>
                {loadingCategories ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} sx={{ color: rengigsPalette.forestGreen }} />
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Categories</InputLabel>
                    <Select
                      multiple
                      value={selectedCategories}
                      onChange={(e) => setSelectedCategories(e.target.value)}
                      label="Categories"
                      sx={{ borderRadius: '12px' }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value.id} 
                              label={value.name} 
                              size="small"
                              sx={{ bgcolor: rengigsPalette.forestGreen, color: '#FFF', fontWeight: 500 }}
                            />
                          ))}
                        </Box>
                      )}
                      disabled={submitting || loadingCategories}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 400,
                            width: 350,
                          },
                        },
                      }}
                    >
                      {categories.length === 0 ? (
                        <MenuItem disabled>No categories available</MenuItem>
                      ) : (
                        categories.map((cat) => (
                          <MenuItem key={cat.id} value={cat} sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Checkbox 
                                checked={selectedCategories.some(c => c.id === cat.id)} 
                                sx={{ color: rengigsPalette.forestGreen, '&.Mui-checked': { color: rengigsPalette.forestGreen } }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: rengigsPalette.charcoal }}>
                                  {cat.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 0.5 }}>
                                  {cat.description}
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}
                <FormHelperText sx={{ color: '#6B7280', mt: 1 }}>
                  Select one or more categories that best describe your solution
                </FormHelperText>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth multiline rows={4} name="description"
                  label="Full Description *" value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth name="tagline" label="Tagline / Short Description"
                  value={formik.values.tagline} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              {/* ── Features & Benefits ── */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.charcoal }}>Key Features</Typography>
                <Divider sx={{ mb: 2 }} />
                {features.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                      fullWidth size="small" value={feature}
                      placeholder={`Feature ${index + 1}`}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      disabled={submitting}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                    <IconButton
                      sx={{ color: rengigsPalette.forestGreen }}
                      onClick={() => handleRemoveFeature(index)}
                      disabled={features.length === 1}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button startIcon={<Add />} onClick={handleAddFeature}
                  sx={{ color: rengigsPalette.forestGreen, fontWeight: 700, textTransform: 'none' }}>
                  Add Feature
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.charcoal }}>Benefits</Typography>
                <Divider sx={{ mb: 2 }} />
                {benefits.map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                      fullWidth size="small" value={benefit}
                      placeholder={`Benefit ${index + 1}`}
                      onChange={(e) => handleBenefitChange(index, e.target.value)}
                      disabled={submitting}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                    <IconButton
                      sx={{ color: rengigsPalette.forestGreen }}
                      onClick={() => handleRemoveBenefit(index)}
                      disabled={benefits.length === 1}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button startIcon={<Add />} onClick={handleAddBenefit}
                  sx={{ color: rengigsPalette.forestGreen, fontWeight: 700, textTransform: 'none' }}>
                  Add Benefit
                </Button>
              </Grid>

              {/* ── Pricing ── */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 800, mt: 2, color: rengigsPalette.charcoal }}>
                  Pricing Structure
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Pricing Model *</InputLabel>
                  <Select
                    name="pricing_model" value={formik.values.pricing_model}
                    onChange={formik.handleChange} label="Pricing Model *"
                    disabled={submitting} sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="one_time">One-time</MenuItem>
                    <MenuItem value="subscription_monthly">Monthly Subscription</MenuItem>
                    <MenuItem value="subscription_yearly">Yearly Subscription</MenuItem>
                    <MenuItem value="usage_based">Usage-based</MenuItem>
                    <MenuItem value="freemium">Freemium</MenuItem>
                    <MenuItem value="custom">Custom Pricing</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth type="number" name="price" label="Price *"
                  value={formik.values.price} onChange={formik.handleChange}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={formik.touched.price && formik.errors.price}
                  disabled={submitting}
                  InputProps={{ startAdornment: <span>$</span> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth name="currency" label="Currency"
                  value={formik.values.currency} onChange={formik.handleChange}
                  disabled={submitting} inputProps={{ maxLength: 3 }} placeholder="USD"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth type="number" name="setup_fee" label="Setup Fee"
                  value={formik.values.setup_fee} onChange={formik.handleChange}
                  disabled={submitting}
                  InputProps={{ startAdornment: <span>$</span> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth type="number" name="trial_days" label="Free Trial Days"
                  value={formik.values.trial_days} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              {/* ── Additional Details ── */}
              <Grid item xs={12}>
                <TextField
                  fullWidth name="implementation_time" label="Implementation Time"
                  placeholder="e.g., 2 weeks, 1 month"
                  value={formik.values.implementation_time} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth name="video_url" label="Demo Video URL"
                  placeholder="https://..."
                  value={formik.values.video_url} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth name="api_documentation" label="API Documentation URL"
                  placeholder="https://..."
                  value={formik.values.api_documentation} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>API Availability</InputLabel>
                  <Select name="api_available" value={formik.values.api_available}
                    onChange={formik.handleChange} label="API Availability"
                    disabled={submitting} sx={{ borderRadius: '12px' }}>
                    <MenuItem value={true}>API Available</MenuItem>
                    <MenuItem value={false}>No API</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Training</InputLabel>
                  <Select name="training_available" value={formik.values.training_available}
                    onChange={formik.handleChange} label="Training"
                    disabled={submitting} sx={{ borderRadius: '12px' }}>
                    <MenuItem value={true}>Training Available</MenuItem>
                    <MenuItem value={false}>No Training</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* ── Actions ── */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4, pt: 3, borderTop: `1px solid ${rengigsPalette.border}` }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/solutions')}
                    disabled={submitting}
                    sx={{
                      borderColor: rengigsPalette.forestGreen,
                      color: rengigsPalette.forestGreen,
                      px: 4, py: 1.2, borderRadius: '12px',
                      fontWeight: 600, textTransform: 'none',
                      '&:hover': { borderColor: rengigsPalette.brandLime, bgcolor: rengigsPalette.softGreen },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting || organizations.length === 0}
                    startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                    sx={{
                      background: rengigsPalette.gradient,
                      backgroundSize: '200% auto',
                      color: rengigsPalette.white,
                      px: 6, py: 1.2, borderRadius: '12px',
                      fontWeight: 700, textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: rengigsPalette.gradientReverse,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 10px 20px -5px ${rengigsPalette.forestGreen}80`,
                      },
                      '&:disabled': { background: rengigsPalette.lightGreen, color: rengigsPalette.white },
                    }}
                  >
                    {submitting ? 'Creating...' : 'Create Solution'}
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </form>
        </Paper>
      </Container>

      {/* LinkedIn-Style Success Dialog (Centered with Auto-Close) */}
      <Dialog
        open={success}
        onClose={handleCloseDialog}
        TransitionComponent={SlideTransition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: rengigsPalette.gradient,
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(46,125,50,0.3), 0 4px 12px rgba(0,0,0,0.1)',
            m: 2,
          }
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {/* Success Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
                animation: 'pulse 0.5s ease-in-out',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.8)', opacity: 0 },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: rengigsPalette.white }} />
            </Box>

            {/* Success Message */}
            <Typography variant="h5" sx={{ fontWeight: 700, color: rengigsPalette.white, mb: 1 }}>
              Solution Created!
            </Typography>
            
            <Typography variant="body1" sx={{ color: rengigsPalette.white, opacity: 0.9, mb: 1, fontWeight: 500 }}>
              {createdSolutionTitle}
            </Typography>
            
            <Typography variant="body2" sx={{ color: rengigsPalette.white, opacity: 0.8, mb: 2 }}>
              {successMessage}
            </Typography>

            {/* Progress indicator for auto-close */}
            <Box sx={{ width: '100%', mb: 3 }}>
              <Box
                sx={{
                  width: '100%',
                  height: '4px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    background: rengigsPalette.white,
                    borderRadius: '2px',
                    animation: 'shrink 4s linear forwards',
                    '@keyframes shrink': {
                      '0%': { width: '100%' },
                      '100%': { width: '0%' },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleViewSolution}
                endIcon={<Launch sx={{ fontSize: 18 }} />}
                sx={{
                  borderColor: rengigsPalette.white,
                  color: rengigsPalette.white,
                  py: 1.2,
                  borderRadius: '10px',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: rengigsPalette.white,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                View Solution
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCloseDialog}
                sx={{
                  backgroundColor: rengigsPalette.white,
                  color: rengigsPalette.forestGreen,
                  py: 1.2,
                  borderRadius: '10px',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: rengigsPalette.white,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                }}
              >
                Dismiss
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateSolutionPage;